'use client';

import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  ListSubheader,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import './CreateOrganisation.css';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import UploadIcon from '../../../../assets/Admin/Organisation/upload.svg';
import CloseIcon from '@mui/icons-material/Close';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { axiosInstance } from '../../../../utils/tokenUtils';
import CalendarComponent from '../../../../components/Calendar/CalendarComponent';
import {
  trimString,
  isValidEmail,
  isValidName,
  isValidPhoneNumber,
  sanitizePhoneDigits,
} from '../../../../components/Validation/Validation';

const countryCodeMap = {
  AF: { code: '+93', name: 'Afghanistan' },
  AL: { code: '+355', name: 'Albania' },
  DZ: { code: '+213', name: 'Algeria' },
  AS: { code: '+1684', name: 'American Samoa' },
  AD: { code: '+376', name: 'Andorra' },
  AO: { code: '+244', name: 'Angola' },
  AI: { code: '+1264', name: 'Anguilla' },
  AG: { code: '+1268', name: 'Antigua and Barbuda' },
  AR: { code: '+54', name: 'Argentina' },
  AM: { code: '+374', name: 'Armenia' },
  AW: { code: '+297', name: 'Aruba' },
  AU: { code: '+61', name: 'Australia' },
  AT: { code: '+43', name: 'Austria' },
  AZ: { code: '+994', name: 'Azerbaijan' },
  BS: { code: '+1242', name: 'Bahamas' },
  BH: { code: '+973', name: 'Bahrain' },
  BD: { code: '+880', name: 'Bangladesh' },
  BB: { code: '+1246', name: 'Barbados' },
  BY: { code: '+375', name: 'Belarus' },
  BE: { code: '+32', name: 'Belgium' },
  BZ: { code: '+501', name: 'Belize' },
  BJ: { code: '+229', name: 'Benin' },
  BM: { code: '+1441', name: 'Bermuda' },
  BT: { code: '+975', name: 'Bhutan' },
  BO: { code: '+591', name: 'Bolivia' },
  BA: { code: '+387', name: 'Bosnia and Herzegovina' },
  BW: { code: '+267', name: 'Botswana' },
  BR: { code: '+55', name: 'Brazil' },
  BN: { code: '+673', name: 'Brunei' },
  BG: { code: '+359', name: 'Bulgaria' },
  BF: { code: '+226', name: 'Burkina Faso' },
  BI: { code: '+257', name: 'Burundi' },
  KH: { code: '+855', name: 'Cambodia' },
  CM: { code: '+237', name: 'Cameroon' },
  CA: { code: '+1', name: 'Canada' },
  CV: { code: '+238', name: 'Cape Verde' },
  KY: { code: '+1345', name: 'Cayman Islands' },
  CF: { code: '+236', name: 'Central African Republic' },
  TD: { code: '+235', name: 'Chad' },
  CL: { code: '+56', name: 'Chile' },
  CN: { code: '+86', name: 'China' },
  CO: { code: '+57', name: 'Colombia' },
  KM: { code: '+269', name: 'Comoros' },
  CG: { code: '+242', name: 'Congo' },
  CD: { code: '+243', name: 'Congo, Democratic Republic' },
  CK: { code: '+682', name: 'Cook Islands' },
  CR: { code: '+506', name: 'Costa Rica' },
  CI: { code: '+225', name: "Cote d'Ivoire" },
  HR: { code: '+385', name: 'Croatia' },
  CU: { code: '+53', name: 'Cuba' },
  CY: { code: '+357', name: 'Cyprus' },
  CZ: { code: '+420', name: 'Czech Republic' },
  DK: { code: '+45', name: 'Denmark' },
  DJ: { code: '+253', name: 'Djibouti' },
  DM: { code: '+1767', name: 'Dominica' },
  DO: { code: '+1', name: 'Dominican Republic' },
  EC: { code: '+593', name: 'Ecuador' },
  EG: { code: '+20', name: 'Egypt' },
  SV: { code: '+503', name: 'El Salvador' },
  GQ: { code: '+240', name: 'Equatorial Guinea' },
  ER: { code: '+291', name: 'Eritrea' },
  EE: { code: '+372', name: 'Estonia' },
  ET: { code: '+251', name: 'Ethiopia' },
  FK: { code: '+500', name: 'Falkland Islands' },
  FO: { code: '+298', name: 'Faroe Islands' },
  FJ: { code: '+679', name: 'Fiji' },
  FI: { code: '+358', name: 'Finland' },
  FR: { code: '+33', name: 'France' },
  GF: { code: '+594', name: 'French Guiana' },
  PF: { code: '+689', name: 'French Polynesia' },
  GA: { code: '+241', name: 'Gabon' },
  GM: { code: '+220', name: 'Gambia' },
  GE: { code: '+995', name: 'Georgia' },
  DE: { code: '+49', name: 'Germany' },
  GH: { code: '+233', name: 'Ghana' },
  GI: { code: '+350', name: 'Gibraltar' },
  GR: { code: '+30', name: 'Greece' },
  GL: { code: '+299', name: 'Greenland' },
  GD: { code: '+1473', name: 'Grenada' },
  GP: { code: '+590', name: 'Guadeloupe' },
  GU: { code: '+1671', name: 'Guam' },
  GT: { code: '+502', name: 'Guatemala' },
  GN: { code: '+224', name: 'Guinea' },
  GW: { code: '+245', name: 'Guinea-Bissau' },
  GY: { code: '+592', name: 'Guyana' },
  HT: { code: '+509', name: 'Haiti' },
  HN: { code: '+504', name: 'Honduras' },
  HK: { code: '+852', name: 'Hong Kong' },
  HU: { code: '+36', name: 'Hungary' },
  IS: { code: '+354', name: 'Iceland' },
  IN: { code: '+91', name: 'India' },
  ID: { code: '+62', name: 'Indonesia' },
  IR: { code: '+98', name: 'Iran' },
  IQ: { code: '+964', name: 'Iraq' },
  IE: { code: '+353', name: 'Ireland' },
  IL: { code: '+972', name: 'Israel' },
  IT: { code: '+39', name: 'Italy' },
  JM: { code: '+1876', name: 'Jamaica' },
  JP: { code: '+81', name: 'Japan' },
  JO: { code: '+962', name: 'Jordan' },
  KZ: { code: '+7', name: 'Kazakhstan' },
  KE: { code: '+254', name: 'Kenya' },
  KI: { code: '+686', name: 'Kiribati' },
  KW: { code: '+965', name: 'Kuwait' },
  KG: { code: '+996', name: 'Kyrgyzstan' },
  LA: { code: '+856', name: 'Laos' },
  LV: { code: '+371', name: 'Latvia' },
  LB: { code: '+961', name: 'Lebanon' },
  LS: { code: '+266', name: 'Lesotho' },
  LR: { code: '+231', name: 'Liberia' },
  LY: { code: '+218', name: 'Libya' },
  LI: { code: '+423', name: 'Liechtenstein' },
  LT: { code: '+370', name: 'Lithuania' },
  LU: { code: '+352', name: 'Luxembourg' },
  MO: { code: '+853', name: 'Macau' },
  MK: { code: '+389', name: 'Macedonia' },
  MG: { code: '+261', name: 'Madagascar' },
  MW: { code: '+265', name: 'Malawi' },
  MY: { code: '+60', name: 'Malaysia' },
  MV: { code: '+960', name: 'Maldives' },
  ML: { code: '+223', name: 'Mali' },
  MT: { code: '+356', name: 'Malta' },
  MH: { code: '+692', name: 'Marshall Islands' },
  MQ: { code: '+596', name: 'Martinique' },
  MR: { code: '+222', name: 'Mauritania' },
  MU: { code: '+230', name: 'Mauritius' },
  YT: { code: '+262', name: 'Mayotte' },
  MX: { code: '+52', name: 'Mexico' },
  FM: { code: '+691', name: 'Micronesia' },
  MD: { code: '+373', name: 'Moldova' },
  MC: { code: '+377', name: 'Monaco' },
  MN: { code: '+976', name: 'Mongolia' },
  ME: { code: '+382', name: 'Montenegro' },
  MS: { code: '+1664', name: 'Montserrat' },
  MA: { code: '+212', name: 'Morocco' },
  MZ: { code: '+258', name: 'Mozambique' },
  MM: { code: '+95', name: 'Myanmar' },
  NA: { code: '+264', name: 'Namibia' },
  NR: { code: '+674', name: 'Nauru' },
  NP: { code: '+977', name: 'Nepal' },
  NL: { code: '+31', name: 'Netherlands' },
  NC: { code: '+687', name: 'New Caledonia' },
  NZ: { code: '+64', name: 'New Zealand' },
  NI: { code: '+505', name: 'Nicaragua' },
  NE: { code: '+227', name: 'Niger' },
  NG: { code: '+234', name: 'Nigeria' },
  NU: { code: '+683', name: 'Niue' },
  NF: { code: '+672', name: 'Norfolk Island' },
  KP: { code: '+850', name: 'North Korea' },
  MP: { code: '+1670', name: 'Northern Mariana Islands' },
  NO: { code: '+47', name: 'Norway' },
  OM: { code: '+968', name: 'Oman' },
  PK: { code: '+92', name: 'Pakistan' },
  PW: { code: '+680', name: 'Palau' },
  PS: { code: '+970', name: 'Palestine' },
  PA: { code: '+507', name: 'Panama' },
  PG: { code: '+675', name: 'Papua New Guinea' },
  PY: { code: '+595', name: 'Paraguay' },
  PE: { code: '+51', name: 'Peru' },
  PH: { code: '+63', name: 'Philippines' },
  PL: { code: '+48', name: 'Poland' },
  PT: { code: '+351', name: 'Portugal' },
  PR: { code: '+1', name: 'Puerto Rico' },
  QA: { code: '+974', name: 'Qatar' },
  RE: { code: '+262', name: 'Reunion' },
  RO: { code: '+40', name: 'Romania' },
  RU: { code: '+7', name: 'Russia' },
  RW: { code: '+250', name: 'Rwanda' },
  KN: { code: '+1869', name: 'Saint Kitts and Nevis' },
  LC: { code: '+1758', name: 'Saint Lucia' },
  VC: { code: '+1784', name: 'Saint Vincent and the Grenadines' },
  WS: { code: '+685', name: 'Samoa' },
  SM: { code: '+378', name: 'San Marino' },
  ST: { code: '+239', name: 'Sao Tome and Principe' },
  SA: { code: '+966', name: 'Saudi Arabia' },
  SN: { code: '+221', name: 'Senegal' },
  RS: { code: '+381', name: 'Serbia' },
  SC: { code: '+248', name: 'Seychelles' },
  SL: { code: '+232', name: 'Sierra Leone' },
  SG: { code: '+65', name: 'Singapore' },
  SK: { code: '+421', name: 'Slovakia' },
  SI: { code: '+386', name: 'Slovenia' },
  SB: { code: '+677', name: 'Solomon Islands' },
  SO: { code: '+252', name: 'Somalia' },
  ZA: { code: '+27', name: 'South Africa' },
  KR: { code: '+82', name: 'South Korea' },
  SS: { code: '+211', name: 'South Sudan' },
  ES: { code: '+34', name: 'Spain' },
  LK: { code: '+94', name: 'Sri Lanka' },
  SD: { code: '+249', name: 'Sudan' },
  SR: { code: '+597', name: 'Suriname' },
  SZ: { code: '+268', name: 'Swaziland' },
  SE: { code: '+46', name: 'Sweden' },
  CH: { code: '+41', name: 'Switzerland' },
  SY: { code: '+963', name: 'Syria' },
  TW: { code: '+886', name: 'Taiwan' },
  TJ: { code: '+992', name: 'Tajikistan' },
  TZ: { code: '+255', name: 'Tanzania' },
  TH: { code: '+66', name: 'Thailand' },
  TL: { code: '+670', name: 'Timor-Leste' },
  TG: { code: '+228', name: 'Togo' },
  TK: { code: '+690', name: 'Tokelau' },
  TO: { code: '+676', name: 'Tonga' },
  TT: { code: '+1868', name: 'Trinidad and Tobago' },
  TN: { code: '+216', name: 'Tunisia' },
  TR: { code: '+90', name: 'Turkey' },
  TM: { code: '+993', name: 'Turkmenistan' },
  TC: { code: '+1649', name: 'Turks and Caicos Islands' },
  TV: { code: '+688', name: 'Tuvalu' },
  UG: { code: '+256', name: 'Uganda' },
  UA: { code: '+380', name: 'Ukraine' },
  AE: { code: '+971', name: 'United Arab Emirates' },
  GB: { code: '+44', name: 'United Kingdom' },
  US: { code: '+1', name: 'United States' },
  UY: { code: '+598', name: 'Uruguay' },
  UZ: { code: '+998', name: 'Uzbekistan' },
  VU: { code: '+678', name: 'Vanuatu' },
  VA: { code: '+39', name: 'Vatican City' },
  VE: { code: '+58', name: 'Venezuela' },
  VN: { code: '+84', name: 'Vietnam' },
  VG: { code: '+1284', name: 'Virgin Islands, British' },
  VI: { code: '+1340', name: 'Virgin Islands, U.S.' },
  WF: { code: '+681', name: 'Wallis and Futuna' },
  YE: { code: '+967', name: 'Yemen' },
  ZM: { code: '+260', name: 'Zambia' },
  ZW: { code: '+263', name: 'Zimbabwe' },
};

// Utility functions for phone number parsing
const parsePhoneNumber = fullPhoneNumber => {
  if (!fullPhoneNumber || !fullPhoneNumber.startsWith('+')) {
    return { countryCode: '+974', phoneNumber: fullPhoneNumber || '' };
  }

  // Sort country codes by length (longest first) for proper matching
  const sortedCodes = Object.values(countryCodeMap)
    .map(country => country.code)
    .sort((a, b) => b.length - a.length);

  for (const code of sortedCodes) {
    if (fullPhoneNumber.startsWith(code)) {
      return {
        countryCode: code,
        phoneNumber: fullPhoneNumber.substring(code.length),
      };
    }
  }

  // Fallback - assume first few digits are country code
  return { countryCode: '+974', phoneNumber: fullPhoneNumber.substring(1) };
};
// --- END COUNTRY CODE MAP AND UTILS ---

const CreateOrganisation = () => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // form state
  const [formData, setFormData] = useState({
    orgName: '',
    orgEmail: '',
    orgContactCode: '+974',
    orgContactNumber: '',
    orgAddress: '',
    orgCity: '',
    orgCountry: '',
    ownerName: '',
    ownerContactCode: '+974',
    ownerContactNumber: '',
    ownerGender: '',
    ownerDOB: null,
    bankName: '',
    bankAddress: '',
    accountHolderName: '',
    iban: '',
    document: null,
  });

  // Document upload state
  const [documents, setDocuments] = useState([{ id: 1, name: '', file: null }]);

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = (field, value) => {
    switch (field) {
      case 'orgName': {
        const v = trimString(value);
        if (!v) return 'Organisation name is required';
        if (!isValidName(v)) return 'Name must contain only letters';
        return '';
      }
      case 'orgEmail': {
        const v = trimString(value).toLowerCase();
        if (!v) return 'Organisation email is required';
        if (!isValidEmail(v)) return 'Invalid email address';
        return '';
      }
      case 'orgContactNumber': {
        const d = sanitizePhoneDigits(value);
        if (!d) return 'Organisation contact number is required';
        if (!isValidPhoneNumber(d, 5, 15)) return 'Phone must be 5–15 digits';
        return '';
      }
      case 'orgAddress': {
        const v = trimString(value);
        if (!v) return 'Organisation address is required';
        return '';
      }
      case 'orgCountry': {
        const v = trimString(value);
        if (!v) return 'Organisation country is required';
        return '';
      }
      case 'orgCity': {
        const v = trimString(value);
        if (!v) return 'Organisation city is required';
        return '';
      }
      case 'ownerName': {
        const v = trimString(value);
        if (!v) return 'Owner name is required';
        if (!isValidName(v)) return 'Name must contain only letters';
        return '';
      }
      case 'ownerContactNumber': {
        const d = sanitizePhoneDigits(value);
        if (!d) return 'Owner contact number is required';
        if (!isValidPhoneNumber(d, 5, 15)) return 'Phone must be 5–15 digits';
        return '';
      }
      case 'ownerGender': {
        const v = trimString(value);
        if (!v) return 'Gender is required';
        return '';
      }
      default:
        return '';
    }
  };

  const handleValidatedChange = (field, rawValue) => {
    setFormData(prev => ({ ...prev, [field]: rawValue }));
    setTouched(prev => ({ ...prev, [field]: true }));
    const message = validateField(field, rawValue);
    setErrors(prev => ({ ...prev, [field]: message }));
  };

  const isFormValid = () => {
    const requiredFields = [
      'orgName',
      'orgEmail',
      'orgContactNumber',
      'orgAddress',
      'orgCountry',
      'orgCity',
      'ownerName',
      'ownerContactNumber',
      'ownerGender',
    ];
    for (const f of requiredFields) {
      const val = formData[f];
      if (validateField(f, val)) return false;
    }
    return true;
  };

  // --- COUNTRY CODE DROPDOWN STATE AND HANDLERS ---
  const [orgCountryCodeDropdownOpen, setOrgCountryCodeDropdownOpen] = useState(false);
  const [orgCountryCodeSearch, setOrgCountryCodeSearch] = useState('');
  const orgCountryCodeDropdownRef = useRef(null);
  const orgCountryCodeSearchRef = useRef(null);

  const [ownerCountryCodeDropdownOpen, setOwnerCountryCodeDropdownOpen] = useState(false);
  const [ownerCountryCodeSearch, setOwnerCountryCodeSearch] = useState('');
  const ownerCountryCodeDropdownRef = useRef(null);
  const ownerCountryCodeSearchRef = useRef(null);

  // Search states for Country/City Select menus
  const [countryMenuSearch, setCountryMenuSearch] = useState('');
  const [cityMenuSearch, setCityMenuSearch] = useState('');

  const getAllCountriesWithDialCodes = () => {
    return Object.entries(countryCodeMap)
      .map(([countryCode, data]) => ({
        countryCode,
        dialCode: data.code,
        name: data.name,
        displayText: `${data.code} (${data.name})`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const getFilteredCountries = search => {
    if (!search.trim()) {
      return getAllCountriesWithDialCodes();
    }
    const searchTerm = search.toLowerCase().trim();
    return getAllCountriesWithDialCodes().filter(
      country =>
        country.name.toLowerCase().includes(searchTerm) ||
        country.dialCode.includes(searchTerm) ||
        country.countryCode.toLowerCase().includes(searchTerm)
    );
  };

  const handleOrgCountryCodeDropdownToggle = () => {
    const newState = !orgCountryCodeDropdownOpen;
    setOrgCountryCodeDropdownOpen(newState);
    if (newState) {
      setTimeout(() => {
        if (orgCountryCodeSearchRef.current) {
          orgCountryCodeSearchRef.current.focus();
        }
      }, 100);
    } else {
      setOrgCountryCodeSearch('');
    }
  };

  const handleOwnerCountryCodeDropdownToggle = () => {
    const newState = !ownerCountryCodeDropdownOpen;
    setOwnerCountryCodeDropdownOpen(newState);
    if (newState) {
      setTimeout(() => {
        if (ownerCountryCodeSearchRef.current) {
          ownerCountryCodeSearchRef.current.focus();
        }
      }, 100);
    } else {
      setOwnerCountryCodeSearch('');
    }
  };

  const handleOrgCountryCodeSelect = dialCode => {
    setFormData(prev => ({ ...prev, orgContactCode: dialCode }));
    setOrgCountryCodeDropdownOpen(false);
    setOrgCountryCodeSearch('');
  };

  const handleOwnerCountryCodeSelect = dialCode => {
    setFormData(prev => ({ ...prev, ownerContactCode: dialCode }));
    setOwnerCountryCodeDropdownOpen(false);
    setOwnerCountryCodeSearch('');
  };

  const handleOrgCountryCodeSearchKeyDown = e => {
    if (e.key === 'Escape') {
      setOrgCountryCodeDropdownOpen(false);
      setOrgCountryCodeSearch('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const filteredCountries = getFilteredCountries(orgCountryCodeSearch);
      if (filteredCountries.length > 0) {
        handleOrgCountryCodeSelect(filteredCountries[0].dialCode);
      }
    }
  };

  const handleOwnerCountryCodeSearchKeyDown = e => {
    if (e.key === 'Escape') {
      setOwnerCountryCodeDropdownOpen(false);
      setOwnerCountryCodeSearch('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const filteredCountries = getFilteredCountries(ownerCountryCodeSearch);
      if (filteredCountries.length > 0) {
        handleOwnerCountryCodeSelect(filteredCountries[0].dialCode);
      }
    }
  };
  // --- END COUNTRY CODE DROPDOWN STATE AND HANDLERS ---

  // Country/city state for dropdowns
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      setCountryLoading(true);
      try {
        const response = await axiosInstance.get('/auth/countries');
        setCountries(response.data);
      } catch (error) {
        toast.error('Failed to load countries');
        setCountries([]);
      } finally {
        setCountryLoading(false);
      }
    };
    fetchCountries();
  }, []);

  // Fetch cities when country changes
  useEffect(() => {
    if (!formData.orgCountry) {
      setCities([]);
      return;
    }
    const selectedCountry = countries.find(c => c.name === formData.orgCountry);
    if (!selectedCountry) {
      setCities([]);
      return;
    }
    setCityLoading(true);
    axiosInstance
      .get(`/auth/countries/${selectedCountry.id}/cities`)
      .then(res => setCities(res.data))
      .catch(() => {
        toast.error('Failed to load cities');
        setCities([]);
      })
      .finally(() => setCityLoading(false));
  }, [formData.orgCountry, countries]);

  const handleChange = field => e => {
    handleValidatedChange(field, e.target.value);
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, document: file }));
  };

  const handleDocumentNameChange = (id, value) => {
    setDocuments(prev => prev.map(doc => (doc.id === id ? { ...doc, name: value } : doc)));
  };

  const handleDocumentFileChange = (id, file) => {
    setDocuments(prev => prev.map(doc => (doc.id === id ? { ...doc, file } : doc)));
  };

  const handleAddDocument = () => {
    const newId = documents.length > 0 ? Math.max(...documents.map(doc => doc.id)) + 1 : 1;
    setDocuments([...documents, { id: newId, name: '', file: null }]);
  };

  const handleRemoveDocument = id => {
    setDocuments(prev => prev.filter(doc => doc.id !== id));
  };

  const handleClearOrgDocument = id => {
    setDocuments(prev => prev.map(doc => (doc.id === id ? { ...doc, name: '', file: null } : doc)));
  };

  // Validation functions
  // const validateField = (field, value) => {
  //   switch (field) {
  //     case 'orgName':
  //       if (!value) return 'Organisation name is required';
  //       return '';
  //     case 'orgEmail':
  //       if (!value) return 'Organisation email is required';
  //       // Simple email regex
  //       if (!/^\S+@\S+\.\S+$/.test(value)) return 'Invalid email address';
  //       return '';
  //     case 'orgContactCode':
  //       if (!value) return 'Contact code is required';
  //       if (!/^\+?\d{1,4}$/.test(value)) return 'Invalid code';
  //       return '';
  //     case 'orgContactNumber':
  //       if (!value) return 'Contact number is required';
  //       if (!/^\d{5,12}$/.test(value)) return 'Invalid number';
  //       return '';
  //     case 'orgAddress':
  //       if (!value) return 'Organisation address is required';
  //       return '';
  //     case 'orgCity':
  //       if (!value) return 'Organisation city is required';
  //       return '';
  //     case 'orgCountry':
  //       if (!value) return 'Organisation country is required';
  //       return '';
  //     case 'ownerName':
  //       if (!value) return 'Owner name is required';
  //       return '';
  //     case 'ownerContactCode':
  //       if (!value) return 'Contact code is required';
  //       if (!/^\+?\d{1,4}$/.test(value)) return 'Invalid code';
  //       return '';
  //     case 'ownerContactNumber':
  //       if (!value) return 'Contact number is required';
  //       if (!/^\d{5,12}$/.test(value)) return 'Invalid number';
  //       return '';
  //     case 'ownerGender':
  //       if (!value) return 'Gender is required';
  //       return '';
  //     case 'iban':
  //       if (formData.bankName || formData.accountHolderName || formData.bankAddress) {
  //         if (!value) return 'IBAN is required';
  //         if (!/^\d{8,34}$/.test(value)) return 'Invalid IBAN';
  //       }
  //       return '';
  //     default:
  //       return '';
  //   }
  // };

  // Validate all fields on change
  // useEffect(() => {
  //   const newErrors = {};
  //   Object.keys(formData).forEach(field => {
  //     if (
  //       [
  //         'orgName',
  //         'orgEmail',
  //         'orgContactCode',
  //         'orgContactNumber',
  //         'orgAddress',
  //         'orgCity',
  //         'orgCountry',
  //         'ownerName',
  //         'ownerContactCode',
  //         'ownerContactNumber',
  //         'ownerGender',
  //         'iban',
  //       ].includes(field)
  //     ) {
  //       const error = validateField(field, formData[field]);
  //       if (error) newErrors[field] = error;
  //     }
  //   });
  //   setErrors(newErrors);
  // }, [formData]);

  // const isFormValid =
  //   formData.orgName &&
  //   formData.orgEmail &&
  //   formData.orgContactCode &&
  //   formData.orgContactNumber &&
  //   formData.orgAddress &&
  //   formData.orgCity &&
  //   formData.orgCountry &&
  //   formData.ownerName &&
  //   formData.ownerContactCode &&
  //   formData.ownerContactNumber &&
  //   formData.ownerGender &&
  //   Object.keys(errors).length === 0;

  // Upload document to server and get URL
  const uploadDocument = async (file, documentType, orgName) => {
    const formData = new FormData();
    formData.append('file', file);
    // Use query params for org_name and document_type (fix)
    const params = new URLSearchParams({
      org_name: orgName,
      document_type: documentType,
    });
    try {
      const response = await axiosInstance.post(
        `/admin/organisation/upload-document?${params.toString()}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data.document_url;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw new Error(
        `Failed to upload ${documentType}: ${error.response?.data?.detail || error.message}`
      );
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // if (!isFormValid) {
    //   toast.error('Please fill in all required fields');
    //   return;
    // }

    setSubmitting(true);

    try {
      // Clean and validate core fields
      const orgNameClean = trimString(formData.orgName);
      const orgEmailClean = trimString(formData.orgEmail).toLowerCase();
      const orgCodeClean = trimString(formData.orgContactCode || '+974');
      const orgPhoneDigits = sanitizePhoneDigits(formData.orgContactNumber);

      const ownerNameClean = trimString(formData.ownerName);
      const ownerCodeClean = trimString(formData.ownerContactCode || '+974');
      const ownerPhoneDigits = sanitizePhoneDigits(formData.ownerContactNumber);

      if (!orgNameClean || !isValidName(orgNameClean)) {
        toast.error('Please enter a valid organisation name (letters only)');
        return;
      }
      if (!orgEmailClean || !isValidEmail(orgEmailClean)) {
        toast.error('Please enter a valid email address');
        return;
      }
      if (!isValidPhoneNumber(orgPhoneDigits, 5, 15)) {
        toast.error('Please enter a valid organisation phone number');
        return;
      }
      if (!ownerNameClean || !isValidName(ownerNameClean)) {
        toast.error('Please enter a valid owner name (letters only)');
        return;
      }
      if (!isValidPhoneNumber(ownerPhoneDigits, 5, 15)) {
        toast.error('Please enter a valid owner phone number');
        return;
      }

      // Prepare the request payload
      const payload = {
        org_name: orgNameClean,
        org_email: orgEmailClean,
        org_phone_no: `${orgCodeClean}${orgPhoneDigits}`,
        org_address: trimString(formData.orgAddress),
        org_city: trimString(formData.orgCity),
        org_country: trimString(formData.orgCountry),
        owner_name: ownerNameClean,
        owner_phone_no: `${ownerCodeClean}${ownerPhoneDigits}`,
        owner_gender: trimString(formData.ownerGender),
        owner_dob: formData.ownerDOB ? format(formData.ownerDOB, 'yyyy-MM-dd') : null,
      };

      // Add bank details if provided
      if (
        formData.bankName &&
        formData.accountHolderName &&
        formData.iban &&
        formData.bankAddress
      ) {
        payload.bank_name = trimString(formData.bankName);
        payload.bank_address = trimString(formData.bankAddress);
        payload.account_holder_name = trimString(formData.accountHolderName);
        payload.iban = trimString(formData.iban);

        // Upload bank document if provided
        if (formData.document) {
          payload.bank_document_url = await uploadDocument(
            formData.document,
            'bank_document',
            orgNameClean
          );
        }
      }

      // Upload organization documents
      const orgDocuments = {};
      for (const doc of documents) {
        if (trimString(doc.name) && doc.file) {
          const documentUrl = await uploadDocument(doc.file, 'org_document', orgNameClean);
          orgDocuments[trimString(doc.name)] = documentUrl;
        }
      }

      // Add organization documents to payload only if documents were uploaded
      if (Object.keys(orgDocuments).length > 0) {
        payload.org_documents = orgDocuments;
      }

      // Make the API call
      const response = await axiosInstance.post('/admin/organisation', payload);

      toast.success('Organization created successfully!');
      navigate('/admin/organisation');
    } catch (error) {
      console.error('Error creating organization:', error);
      const errorMessage =
        error.response?.data?.detail || error.message || 'Failed to create organization';

      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBreadcrumbClick = () => {
    navigate('/admin/organisation');
  };

  // Helper to show warning toast for a field
  // const showFieldWarning = (field, value) => {
  //   const error = validateField(field, value);
  //   if (error) {
  //     toast.dismiss(field); // Remove any previous toast for this field
  //     // toast.error(error, { id: field });
  //   } else {
  //     toast.dismiss(field);
  //   }
  // };

  // Calendar modal state
  const [dobCalendarOpen, setDobCalendarOpen] = useState(false);

  return loading ? (
    <Box className="createorg-loader-wrapper">
      <CircularProgress />
    </Box>
  ) : (
    <Layout title="Klass Online | Create Organisation">
      <Box className="createorg-page">
        <Box className="createorg-sticky-header">
          <AdminNavbar />
        </Box>
        <Box className="createorg-content-wrapper">
          <Box className="createorg-breadcrumb">
            <span className="createorg-breadcrumb-link" onClick={handleBreadcrumbClick}>
              Organization
            </span>
            <span className="createorg-breadcrumb-separator"> &gt;&gt; </span>
            <span className="createorg-breadcrumb-current">Add New</span>
          </Box>

          <Typography className="createorg-page-title">Create New Organisation</Typography>

          <form className="createorg-form" onSubmit={handleSubmit}>
            {/* Organisation Details */}
            <Box className="createorg-form-section">
              <Typography className="createorg-section-title">Organisation Details</Typography>
              <Box className="createorg-fields-grid">
                <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">Organisation Name</Typography>
                  <TextField
                    placeholder="Name"
                    value={formData.orgName}
                    onChange={handleChange('orgName')}
                    error={touched.orgName && !!errors.orgName}
                    fullWidth
                    className="createorg-form-input"
                    variant="outlined"
                  />
                  {touched.orgName && errors.orgName && (
                    <Typography className="error-text">{errors.orgName}</Typography>
                  )}
                </Box>
                <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">Organisation Email</Typography>
                  <TextField
                    placeholder="example@example.com"
                    value={formData.orgEmail}
                    onChange={handleChange('orgEmail')}
                    type="text"
                    error={touched.orgEmail && !!errors.orgEmail}
                    fullWidth
                    className="createorg-form-input"
                    variant="outlined"
                  />
                  {touched.orgEmail && errors.orgEmail && (
                    <Typography className="error-text">{errors.orgEmail}</Typography>
                  )}
                </Box>
                <Box className="createorg-field-wrapper createorg-contact-field-wrapper">
                  <Typography className="createorg-field-label">Organisation Contact</Typography>
                  <Box className="createorg-contact-fields phone-input-container">
                    <div
                      className={`country-code-dropdown-container ${orgCountryCodeDropdownOpen ? 'open' : ''}`}
                      ref={orgCountryCodeDropdownRef}
                    >
                      <div
                        className="country-code-display-button"
                        onClick={handleOrgCountryCodeDropdownToggle}
                      >
                        <span>{formData.orgContactCode || '+974'}</span>
                        <span className="dropdown-arrow">▼</span>
                      </div>
                      {orgCountryCodeDropdownOpen && (
                        <div className="country-code-dropdown">
                          <div className="country-code-search-container">
                            <input
                              ref={orgCountryCodeSearchRef}
                              type="text"
                              placeholder="Search countries..."
                              value={orgCountryCodeSearch}
                              onChange={e => setOrgCountryCodeSearch(e.target.value)}
                              className="country-code-search-input"
                              onKeyDown={handleOrgCountryCodeSearchKeyDown}
                            />
                          </div>
                          <div className="country-code-dropdown-content">
                            {getFilteredCountries(orgCountryCodeSearch).length > 0 ? (
                              getFilteredCountries(orgCountryCodeSearch).map((country, index) => (
                                <div
                                  key={`${country.dialCode}-${country.countryCode}-${index}`}
                                  className={`country-code-option ${formData.orgContactCode === country.dialCode ? 'selected' : ''}`}
                                  onClick={() => handleOrgCountryCodeSelect(country.dialCode)}
                                >
                                  {country.displayText}
                                </div>
                              ))
                            ) : (
                              <div className="country-code-no-results">No countries found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={formData.orgContactNumber}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value.length <= 15) {
                          handleValidatedChange('orgContactNumber', value);
                        }
                      }}
                      className="phone-number-input createorg-form-input createorg-contact-number"
                      placeholder="Phone Number"
                      maxLength={15}
                    />
                    {touched.orgContactNumber && errors.orgContactNumber && (
                      <Typography className="error-text">{errors.orgContactNumber}</Typography>
                    )}
                  </Box>
                </Box>
                <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">Organisation Address</Typography>
                  <TextField
                    placeholder="Street Name, State, PIN"
                    value={formData.orgAddress}
                    onChange={handleChange('orgAddress')}
                    error={touched.orgAddress && !!errors.orgAddress}
                    fullWidth
                    className="createorg-form-input"
                    variant="outlined"
                  />
                  {touched.orgAddress && errors.orgAddress && (
                    <Typography className="error-text">{errors.orgAddress}</Typography>
                  )}
                </Box>
                <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">Organisation Country</Typography>
                  <TextField
                    select
                    value={formData.orgCountry}
                    onChange={e => {
                      const v = e.target.value;
                      setFormData(prev => ({ ...prev, orgCountry: v, orgCity: '' }));
                      setTouched(prev => ({ ...prev, orgCountry: true }));
                      setErrors(prev => ({
                        ...prev,
                        orgCountry: validateField('orgCountry', v),
                        orgCity: '',
                      }));
                    }}
                    className="createorg-form-input"
                    variant="outlined"
                    fullWidth
                    error={touched.orgCountry && !!errors.orgCountry}
                    SelectProps={{
                      displayEmpty: true,
                      MenuProps: {
                        PaperProps: {
                          className: 'createorg-select-menu-paper createorg-country-menu-paper',
                        },
                      },
                      onOpen: () => setCountryMenuSearch(''),
                      renderValue: selected =>
                        selected ? (
                          selected
                        ) : (
                          <span className="createorg-dropdown-placeholder">Select country</span>
                        ),
                    }}
                  >
                    <ListSubheader disableSticky className="createorg-menu-search-header">
                      <TextField
                        autoFocus
                        value={countryMenuSearch}
                        onChange={e => setCountryMenuSearch(e.target.value)}
                        placeholder="Search country..."
                        size="small"
                        fullWidth
                        onKeyDown={e => {
                          e.stopPropagation();
                        }}
                        className="createorg-menu-search-input"
                      />
                    </ListSubheader>
                    <MenuItem value="">Select country</MenuItem>
                    {countryLoading ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : (
                      countries
                        .filter(country =>
                          country.name
                            .toLowerCase()
                            .includes((countryMenuSearch || '').toLowerCase())
                        )
                        .map(country => (
                          <MenuItem key={country.id} value={country.name}>
                            {country.name}
                          </MenuItem>
                        ))
                    )}
                  </TextField>
                  {touched.orgCountry && errors.orgCountry && (
                    <Typography className="error-text">{errors.orgCountry}</Typography>
                  )}
                </Box>
                {formData.orgCountry && (
                  <Box className="createorg-field-wrapper">
                    <Typography className="createorg-field-label">Organisation City</Typography>
                    <TextField
                      select
                      value={formData.orgCity}
                      onChange={e => {
                        const v = e.target.value;
                        setFormData(prev => ({ ...prev, orgCity: v }));
                        setTouched(prev => ({ ...prev, orgCity: true }));
                        setErrors(prev => ({ ...prev, orgCity: validateField('orgCity', v) }));
                      }}
                      className="createorg-form-input"
                      variant="outlined"
                      fullWidth
                      error={touched.orgCity && !!errors.orgCity}
                      SelectProps={{
                        displayEmpty: true,
                        MenuProps: {
                          PaperProps: {
                            className: 'createorg-select-menu-paper createorg-city-menu-paper',
                          },
                        },
                        onOpen: () => setCityMenuSearch(''),
                        renderValue: selected =>
                          selected ? (
                            selected
                          ) : (
                            <span className="createorg-dropdown-placeholder">Select city</span>
                          ),
                      }}
                    >
                      <ListSubheader disableSticky className="createorg-menu-search-header">
                        <TextField
                          autoFocus
                          value={cityMenuSearch}
                          onChange={e => setCityMenuSearch(e.target.value)}
                          placeholder="Search city..."
                          size="small"
                          fullWidth
                          onKeyDown={e => {
                            e.stopPropagation();
                          }}
                          className="createorg-menu-search-input"
                        />
                      </ListSubheader>
                      <MenuItem value="">Select city</MenuItem>
                      {cityLoading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        cities
                          .filter(city =>
                            city.name.toLowerCase().includes((cityMenuSearch || '').toLowerCase())
                          )
                          .map(city => (
                            <MenuItem key={city.id} value={city.name}>
                              {city.name}
                            </MenuItem>
                          ))
                      )}
                    </TextField>
                    {touched.orgCity && errors.orgCity && (
                      <Typography className="error-text">{errors.orgCity}</Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Owner Details */}
            <Box className="createorg-form-section">
              <Typography className="createorg-section-title">Owner Details</Typography>
              <Box className="createorg-fields-grid">
                <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">Owner Name</Typography>
                  <TextField
                    placeholder="Name"
                    value={formData.ownerName}
                    onChange={handleChange('ownerName')}
                    error={touched.ownerName && !!errors.ownerName}
                    fullWidth
                    className="createorg-form-input"
                    variant="outlined"
                  />
                  {touched.ownerName && errors.ownerName && (
                    <Typography className="error-text">{errors.ownerName}</Typography>
                  )}
                </Box>
                <Box className="createorg-field-wrapper createorg-contact-field-wrapper">
                  <Typography className="createorg-field-label">Owner Contact</Typography>
                  <Box className="createorg-contact-fields phone-input-container">
                    <div
                      className={`country-code-dropdown-container ${ownerCountryCodeDropdownOpen ? 'open' : ''}`}
                      ref={ownerCountryCodeDropdownRef}
                    >
                      <div
                        className="country-code-display-button"
                        onClick={handleOwnerCountryCodeDropdownToggle}
                      >
                        <span>{formData.ownerContactCode || '+974'}</span>
                        <span className="dropdown-arrow">▼</span>
                      </div>
                      {ownerCountryCodeDropdownOpen && (
                        <div className="country-code-dropdown">
                          <div className="country-code-search-container">
                            <input
                              ref={ownerCountryCodeSearchRef}
                              type="text"
                              placeholder="Search countries..."
                              value={ownerCountryCodeSearch}
                              onChange={e => setOwnerCountryCodeSearch(e.target.value)}
                              className="country-code-search-input"
                              onKeyDown={handleOwnerCountryCodeSearchKeyDown}
                            />
                          </div>
                          <div className="country-code-dropdown-content">
                            {getFilteredCountries(ownerCountryCodeSearch).length > 0 ? (
                              getFilteredCountries(ownerCountryCodeSearch).map((country, index) => (
                                <div
                                  key={`${country.dialCode}-${country.countryCode}-${index}`}
                                  className={`country-code-option ${formData.ownerContactCode === country.dialCode ? 'selected' : ''}`}
                                  onClick={() => handleOwnerCountryCodeSelect(country.dialCode)}
                                >
                                  {country.displayText}
                                </div>
                              ))
                            ) : (
                              <div className="country-code-no-results">No countries found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={formData.ownerContactNumber}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value.length <= 15) {
                          handleValidatedChange('ownerContactNumber', value);
                        }
                      }}
                      className="phone-number-input createorg-form-input createorg-contact-number"
                      placeholder="Phone Number"
                      maxLength={15}
                    />
                    {touched.ownerContactNumber && errors.ownerContactNumber && (
                      <Typography className="error-text">{errors.ownerContactNumber}</Typography>
                    )}
                  </Box>
                </Box>
                <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">Gender</Typography>
                  <FormControl
                    fullWidth
                    className="createorg-form-input"
                    error={touched.ownerGender && !!errors.ownerGender}
                  >
                    <Select
                      value={formData.ownerGender}
                      onChange={e => handleValidatedChange('ownerGender', e.target.value)}
                      displayEmpty
                      renderValue={selected =>
                        selected ? (
                          selected.charAt(0).toUpperCase() + selected.slice(1)
                        ) : (
                          <span className="createorg-dropdown-placeholder">Select Gender</span>
                        )
                      }
                      inputProps={{ 'aria-label': 'Gender' }}
                      className="createorg-select-input"
                    >
                      <MenuItem value="" disabled>
                        Select Gender
                      </MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                    </Select>
                  </FormControl>
                  {touched.ownerGender && errors.ownerGender && (
                    <Typography className="error-text">{errors.ownerGender}</Typography>
                  )}
                </Box>
                <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">Date of Birth</Typography>
                  <Box className="createorg-dob-field">
                    <TextField
                      value={formData.ownerDOB ? format(formData.ownerDOB, 'dd/MM/yyyy') : ''}
                      placeholder="DD/MM/YYYY"
                      fullWidth
                      className="createorg-form-input"
                      variant="outlined"
                      inputProps={{ readOnly: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Open calendar"
                              onClick={() => setDobCalendarOpen(true)}
                            >
                              <CalendarTodayIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <CalendarComponent
                      onDateSelect={date => {
                        setFormData(prev => ({ ...prev, ownerDOB: date }));
                        setDobCalendarOpen(false);
                      }}
                      selectedDate={formData.ownerDOB}
                      minDate={new Date(1900, 0, 1)}
                      maxDate={new Date()}
                      isOpen={dobCalendarOpen}
                      onClose={() => setDobCalendarOpen(false)}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Bank Details */}
            <Box className="createorg-form-section">
              <Typography className="createorg-section-title">Bank Details</Typography>
              <FormHelperText className="createorg-optional-text">Optional</FormHelperText>
              <Box className="createorg-fields-grid">
                <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">Bank Name</Typography>
                  <TextField
                    placeholder="Name"
                    value={formData.bankName}
                    onChange={handleChange('bankName')}
                    fullWidth
                    className="createorg-form-input"
                    variant="outlined"
                  />
                </Box>
                <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">Bank Address</Typography>
                  <TextField
                    placeholder="Address"
                    value={formData.bankAddress}
                    onChange={handleChange('bankAddress')}
                    fullWidth
                    className="createorg-form-input"
                    variant="outlined"
                  />
                </Box>
                <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">Account Holder Name</Typography>
                  <TextField
                    placeholder="Full Name"
                    value={formData.accountHolderName}
                    onChange={handleChange('accountHolderName')}
                    fullWidth
                    className="createorg-form-input"
                    variant="outlined"
                  />
                </Box>
                <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">IBAN Number</Typography>
                  <TextField
                    placeholder="XXXX"
                    value={formData.iban}
                    onChange={e => {
                      const raw = e.target.value || '';
                      const cleaned = raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                      setFormData(prev => ({ ...prev, iban: cleaned.slice(0, 34) }));
                    }}
                    fullWidth
                    className="createorg-form-input"
                    variant="outlined"
                    inputProps={{ maxLength: 34, pattern: '[A-Za-z0-9]*' }}
                  />
                </Box>
                {/* <Box className="createorg-field-wrapper">
                  <Typography className="createorg-field-label">Bank Document</Typography>
                  <Box className="createorg-file-upload-wrapper">
                    <input
                      id="bank-document-upload"
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleFileChange}
                      hidden
                    />
                    <Box className="createorg-document-upload-box">
                      {formData.document ? (
                        <Box className="createorg-uploaded-file">
                          <FileIcon className="createorg-file-icon" />
                          <Typography className="createorg-file-name">
                            {formData.document.name}
                          </Typography>
                          <Box className="createorg-file-actions">
                            <a
                              href={URL.createObjectURL(formData.document)}
                              target="_blank"
                              rel="noreferrer"
                              className="createorg-file-view-link"
                            >
                              View
                            </a>
                          </Box>
                        </Box>
                      ) : (
                        <label htmlFor="bank-document-upload" className="createorg-upload-label">
                          <Typography className="createorg-upload-placeholder">Upload</Typography>
                          <img src={UploadIcon} alt="Upload" className="createorg-upload-icon" />
                        </label>
                      )}
                    </Box>
                  </Box>
                </Box> */}
              </Box>
            </Box>

            {/* Document Upload Section */}
            <Box className="createorg-form-section">
              <Typography className="createorg-section-title">Upload Relevant Document</Typography>
              <Box className="createorg-documents-container">
                {documents.map((doc, index) => (
                  <Box key={doc.id} className="createorg-document-row">
                    <Box className="createorg-document-field">
                      <Typography className="createorg-field-label">Document Name</Typography>
                      <TextField
                        placeholder="Name"
                        value={doc.name}
                        onChange={e => handleDocumentNameChange(doc.id, e.target.value)}
                        fullWidth
                        className="createorg-form-input"
                        variant="outlined"
                      />
                    </Box>
                    <Box className="createorg-document-field">
                      <Typography className="createorg-field-label">Upload</Typography>
                      <Box className="createorg-file-upload-wrapper">
                        <input
                          id={`document-upload-${doc.id}`}
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={e => handleDocumentFileChange(doc.id, e.target.files[0])}
                          hidden
                        />
                        <Box className="createorg-document-upload-box">
                          {doc.file ? (
                            <Box className="createorg-uploaded-file">
                              <FileIcon className="createorg-file-icon" />
                              <Typography className="createorg-file-name">
                                {doc.file.name}
                              </Typography>
                              <Box className="createorg-file-actions">
                                <a
                                  href={URL.createObjectURL(doc.file)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="createorg-file-view-link"
                                >
                                  View
                                </a>
                              </Box>
                              <IconButton
                                className="createorg-clear-file"
                                aria-label="Clear file"
                                onClick={() => handleRemoveDocument(doc.id)}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                          ) : (
                            <label
                              htmlFor={`document-upload-${doc.id}`}
                              className="createorg-upload-label"
                            >
                              <Typography className="createorg-upload-placeholder">
                                Upload
                              </Typography>
                              <img
                                src={UploadIcon}
                                alt="Upload"
                                className="createorg-upload-icon-right"
                              />
                            </label>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    {documents.length > 1 && (
                      <IconButton
                        className="createorg-remove-document"
                        onClick={() => handleRemoveDocument(doc.id)}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Box className="createorg-add-document-container">
                  <Button
                    className="createorg-add-document-btn"
                    onClick={handleAddDocument}
                    startIcon={<span>+</span>}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box className="createorg-actions-bar">
              <Button
                className="createorg-cancel-btn"
                type="button"
                onClick={handleBreadcrumbClick}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                className="createorg-create-btn"
                type="submit"
                disabled={submitting || !isFormValid()}
                onClick={e => {
                  // if (!isFormValid) {
                  //   e.preventDefault();
                  //   // List missing required fields
                  //   const requiredFields = [
                  //     { key: 'orgName', label: 'Organisation Name' },
                  //     { key: 'orgEmail', label: 'Organisation Email' },
                  //     // { key: 'orgContactCode', label: 'Organisation Contact Code' },
                  //     // { key: 'orgContactNumber', label: 'Organisation Contact Number' },
                  //     // { key: 'orgAddress', label: 'Organisation Address' },
                  //     // { key: 'orgCity', label: 'Organisation City' },
                  //     // { key: 'orgCountry', label: 'Organisation Country' },
                  //     // { key: 'ownerName', label: 'Owner Name' },
                  //     // { key: 'ownerContactCode', label: 'Owner Contact Code' },
                  //     // { key: 'ownerContactNumber', label: 'Owner Contact Number' },
                  //     // { key: 'ownerGender', label: 'Owner Gender' },
                  //   ];
                  //   const missingFields = requiredFields
                  //     .filter(f => !formData[f.key] || errors[f.key])
                  //     .map(f => f.label);
                  //   toast.error(
                  //     `Please fill in the following required fields: ${missingFields.join(', ')}`
                  //   );
                  // }
                }}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Create'}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Layout>
  );
};

export default CreateOrganisation;
